/*
[GNU Affero General Public License (AGPL)](https://www.gnu.org/licenses/agpl-3.0.html)

VeloceUI - A UI component library for Angular v17+ made with care by Colibryx Srl in Italy.
Copyright (C) 2025 Colibryx S.r.l.

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

This license applies to any accompanying files located directly in the same directory.
*/

import {
	ChangeDetectionStrategy,
	Component,
	TemplateRef,
	type Type,
	contentChild,
	contentChildren,
	input,
	output,
} from "@angular/core";
import { DestinationInputComponent } from "@app/widgets/app/destination-input/destination-input.component";
import { ButtonGroupComponent } from "@app/widgets/base/button-group/button-group.component";
import { CheckboxComponent } from "@app/widgets/base/checkbox/checkbox/checkbox.component";
import { CodeComponent } from "@app/widgets/base/code/code.component";
import { DatepickerComponent } from "@app/widgets/base/datepicker/datepicker.component";
import { FileComponent } from "@app/widgets/base/file/file.component";
import { InputNumberComponent } from "@app/widgets/base/input-number/input-number.component";
import { InputPhoneComponent } from "@app/widgets/base/input-phone/input-phone.component";
import { InputComponent } from "@app/widgets/base/input/input.component";
import { SelectComponent } from "@app/widgets/base/select/select.component";
import { TagsInputComponent } from "@app/widgets/base/tags-input/tags-input.component";
import { TextareaComponent } from "@app/widgets/base/textarea/textarea.component";
import { TimepickerComponent } from "@app/widgets/base/timepicker/timepicker.component";
import { ToggleComponent } from "@app/widgets/base/toggle/toggle.component";
import { v4 as uuidv4 } from "uuid";

// biome-ignore lint/suspicious/noExplicitAny: <explanation>
export const FORM_ITEMS_COMPONENTS: Record<string, Type<any>> = {
	text: InputComponent,
	time: TimepickerComponent,
	toggle: ToggleComponent,
	number: InputNumberComponent,
	textarea: TextareaComponent,
	checkbox: CheckboxComponent,
	code: CodeComponent,
	phone: InputPhoneComponent,
	select: SelectComponent,
	date: DatepickerComponent,
	tags: TagsInputComponent,
	file: FileComponent,
	buttonGroup: ButtonGroupComponent,
	destination: DestinationInputComponent,
} as const;

export type FormItemData =
	| {
		type: keyof typeof FORM_ITEMS_COMPONENTS;
	}
	| {
		type: "custom";
	}
	| {
		type: "array";
	}
	| {
		type: "link";
		url?: string;
		routerLink?: string;
		align?: "left" | "center" | "right";
	};

@Component({
	selector: "cb-form-item",
	templateUrl: "./form-item.component.html",
	styleUrl: "./form-item.component.scss",
	standalone: false,
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormItemComponent {
	public readonly id: string = uuidv4();

	public readonly placeholder = input<string>();
	public readonly label = input<string>();
	public readonly hint = input<string>();
	public readonly info = input<string>();
	public readonly data = input<FormItemData>({ type: "text" });
	public readonly name = input<string>();
	// biome-ignore lint/suspicious/noExplicitAny: <explanation>
	public readonly editorOptions = input<Record<string, any>>({});

	public readonly templateRef = contentChild("ItemTemplate", {
		read: TemplateRef,
	});

	public readonly beforeTemplateRef = contentChild("ItemBeforeTemplate", {
		read: TemplateRef,
	});

	public readonly afterTemplateRef = contentChild("ItemAfterTemplate", {
		read: TemplateRef,
	});

	public readonly selectOptionTemplateRef = contentChild(
		"SelectOptionTemplate",
		{ read: TemplateRef },
	);

	public readonly selectValueTemplateRef = contentChild("SelectValueTemplate", {
		read: TemplateRef,
	});

	public readonly children = contentChildren(FormItemComponent);

	public change = output<unknown>();
	public blur = output<unknown>();
}
